import is from '@sindresorhus/is';
import { z } from 'zod';

import { type LanguageCodeForJSA, languageCodeSchemaForJSA } from '~/shared/internationalization';

import type { ProfileTaskParentContext } from '../model/profile-task';
import {
    isWholeOrNoProfileAddress,
    optionalProfileAddressShape,
    profileAddressMessage,
    segmentIdSchema,
    toProfileAddress,
} from './automation-tile-address';

export const JSA_AUTOMATION_RECOMMENDATION_TILE_TYPE = 'v1.jsa.automation-recommendation' as const;
export const AUTOMATION_RECOMMENDATION_TILE_SLOT_KEY = 'jsa.automation-recommendation';
export const AUTOMATION_RECOMMENDATION_TILE_WIDTH = '500px';

/**
 * One recommended automation of a task. Opened from a job profile it addresses the task like the task
 * tile does; opened from a work task, which belongs to no profile, the parent fields are absent.
 */
export const automationRecommendationTileSchema = z
    .object({
        ...optionalProfileAddressShape,
        automationId: segmentIdSchema,
        language: languageCodeSchemaForJSA.optional(),
        taskId: segmentIdSchema,
        taskVersionId: z.string().trim().min(1).optional(),
        type: z.literal(JSA_AUTOMATION_RECOMMENDATION_TILE_TYPE),
    })
    .refine(isWholeOrNoProfileAddress, { message: profileAddressMessage });

export type AutomationRecommendationTilePayload = z.infer<typeof automationRecommendationTileSchema>;

export const isAutomationRecommendationTilePayload = (
    payload: unknown,
): payload is AutomationRecommendationTilePayload =>
    is.plainObject(payload) && payload['type'] === JSA_AUTOMATION_RECOMMENDATION_TILE_TYPE;

export const createAutomationRecommendationTilePayload = ({
    automationId,
    context,
    language,
    taskId,
    taskVersionId,
}: {
    readonly automationId: string;
    /** The job profile the task is listed on; absent for a work task opened on its own. */
    readonly context?: ProfileTaskParentContext;
    readonly language?: LanguageCodeForJSA;
    readonly taskId: string;
    readonly taskVersionId?: string;
}): AutomationRecommendationTilePayload => ({
    ...toProfileAddress(context),
    automationId,
    ...(is.undefined(language) ? {} : { language }),
    taskId,
    ...(is.undefined(taskVersionId) ? {} : { taskVersionId }),
    type: JSA_AUTOMATION_RECOMMENDATION_TILE_TYPE,
});
